import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Paper, Chip, TextField, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import AppSnackbar from '../../components/shared/AppSnackbar';
import { colors } from '../../theme/theme';

const PREDEFINED_INTERESTS = [
  'AI/ML', 'Design', 'Marketing', 'Finance', 'Startups', 'Creative',
  'Tech', 'Sustainability', 'Product', 'Leadership', 'Freelancing',
  'Photography', 'Blockchain', 'DevOps', 'UX Research', 'Data Science',
];

const PREDEFINED_SKILLS = [
  'Python', 'JavaScript', 'UI/UX', 'Cloud Architecture', 'Data Analysis',
  'Content Marketing', 'Sales', 'Project Management', 'Public Speaking',
  'Graphic Design', 'Mobile Development', 'Machine Learning',
];

export default function InterestsPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();

  const [interests, setInterests] = useState<string[]>(user?.interests || []);
  const [skills, setSkills] = useState<string[]>(user?.skills || []);
  const [lookingFor, setLookingFor] = useState(user?.looking_for || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' });

  const toggleTag = (tag: string, list: string[], setList: (l: string[]) => void) => {
    if (list.includes(tag)) setList(list.filter(t => t !== tag));
    else if (list.length < 8) setList([...list, tag]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await userService.updateProfile({ interests, skills, looking_for: lookingFor || undefined });
      await refreshUser();
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 1500);
    } catch (e: any) { console.warn('[InterestsPage] save interests failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{t('pages.interests.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Help us find your people. This data improves future matching.</Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>Saved! Redirecting to profile...</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>What interests you? (max 8)</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PREDEFINED_INTERESTS.map(tag => (
            <Chip
              key={tag}
              label={tag}
              clickable
              onClick={() => toggleTag(tag, interests, setInterests)}
              sx={{
                bgcolor: interests.includes(tag) ? colors.primary.main : colors.neutral[100],
                color: interests.includes(tag) ? 'white' : colors.neutral[700],
                fontWeight: interests.includes(tag) ? 700 : 400,
                '&:hover': { bgcolor: interests.includes(tag) ? colors.primary.dark : colors.neutral[200] },
              }}
            />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Your skills (max 8)</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PREDEFINED_SKILLS.map(tag => (
            <Chip
              key={tag}
              label={tag}
              clickable
              onClick={() => toggleTag(tag, skills, setSkills)}
              sx={{
                bgcolor: skills.includes(tag) ? colors.secondary.main : colors.neutral[100],
                color: skills.includes(tag) ? 'white' : colors.neutral[700],
                fontWeight: skills.includes(tag) ? 700 : 400,
              }}
            />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>What are you looking for?</Typography>
        <TextField
          fullWidth
          placeholder="e.g., Co-founder for SaaS project, Design mentorship, Just meeting new people"
          value={lookingFor}
          onChange={e => setLookingFor(e.target.value)}
          inputProps={{ maxLength: 200 }}
          multiline
          rows={2}
        />
      </Paper>

      <Button fullWidth variant="contained" size="large" onClick={handleSave} disabled={saving}
        sx={{ py: 1.5, borderRadius: 12 }}>
        {saving ? 'Saving...' : 'Save Interests'}
      </Button>

      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
